import type {
  AIActionKey,
  CustomerActionAllowanceSnapshot,
  CustomerActionAllowanceMap,
  EntitlementDecision,
  EntitlementReasonCode,
  FeatureKey,
  PaidPlanTier,
  PlanSet,
  PlanTier,
  RevenueCatStore,
  SeatAssignmentSummary,
  SeatPoolSummary,
  SubscriptionCatalog,
  SubscriptionSnapshot,
  UsageCreditSnapshot,
  UsageSnapshot,
  WorkspaceSubscriptionSummary,
} from '../../../types/src/subscription';
import {
  comparePlanTiers,
  getHighestPlanTier,
  getModelPolicyForTier,
  normalizeLegacyPlanTier,
  subscriptionCatalog,
} from './catalog';
import { resolveMetering } from './metering';
import {
  buildCustomerAllowanceSnapshot,
  resolveCustomerActionAllowance,
} from './allowances';
import { selectUpgradePrompt } from './prompts';
import { selectModelPolicy } from './routing';

export interface ResolveSubscriptionSnapshotInput {
  planSet: PlanSet;
  legacyPlan?: string | null;
  tier?: string | null;
  status?: string | null;
  billingInterval?: 'monthly' | 'yearly' | null;
  renewsAt?: string | Date | null;
  trialEndsAt?: string | Date | null;
  willRenew?: boolean | null;
  source?: string | null;
  managementUrl?: string | null;
  store?: RevenueCatStore | null;
  lapsedFromTier?: PaidPlanTier | null;
  seat?: {
    companyId: string;
    userId: string;
    tier: string | null;
    assignedAt?: string | Date | null;
    assignedBy?: string | null;
  } | null;
  workspace?: {
    companyId: string;
    workspaceTier?: string | null;
    memberCount?: number;
    seatPool?: Partial<SeatPoolSummary> | null;
    seatAssignments?: Array<{
      companyId: string;
      userId: string;
      tier: string | null;
      assignedAt?: string | Date | null;
      assignedBy?: string | null;
    }> | null;
  } | null;
  usage?:
    | (Partial<Omit<UsageSnapshot, 'credits'>> & {
        credits?: Partial<UsageCreditSnapshot> | null;
      })
    | null;
  actionAllowances?: CustomerActionAllowanceMap | null;
}

function toIsoString(value?: string | Date | null) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.toISOString();
}

function getCycleResetAt(cycleKey: string) {
  const [yearRaw, monthRaw] = cycleKey.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    return new Date().toISOString();
  }

  return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)).toISOString();
}

function minRemaining(...values: Array<number | null>) {
  const numeric = values.filter((value): value is number => value !== null);
  return numeric.length > 0 ? Math.min(...numeric) : null;
}

function buildSeatSummary(
  seat:
    | ResolveSubscriptionSnapshotInput['seat']
    | ResolveSubscriptionSnapshotInput['workspace'] extends infer T
    ? T
    : never,
): SeatAssignmentSummary | null {
  if (
    !seat ||
    typeof seat !== 'object' ||
    !('companyId' in seat) ||
    !('userId' in seat) ||
    !seat.companyId ||
    !seat.userId
  ) {
    return null;
  }

  const tier = normalizeLegacyPlanTier((seat as { tier?: string | null }).tier);
  if (tier === 'free') {
    return null;
  }

  return {
    companyId: seat.companyId,
    userId: seat.userId,
    tier,
    assignedAt:
      toIsoString((seat as { assignedAt?: string | Date | null }).assignedAt) ??
      new Date(0).toISOString(),
    assignedBy: (seat as { assignedBy?: string | null }).assignedBy ?? null,
  };
}

function buildWorkspaceSummary(
  planSet: PlanSet,
  workspace: ResolveSubscriptionSnapshotInput['workspace'],
  catalog: SubscriptionCatalog,
): WorkspaceSubscriptionSummary | null {
  if (planSet !== 'employer' || !workspace?.companyId) {
    return null;
  }

  const normalizedAssignments = (workspace.seatAssignments ?? [])
    .map((assignment) => buildSeatSummary(assignment))
    .filter((assignment): assignment is SeatAssignmentSummary =>
      Boolean(assignment),
    );
  const workspaceTier = getHighestPlanTier([
    workspace.workspaceTier,
    ...normalizedAssignments.map((assignment) => assignment.tier),
  ]);
  const purchased = {
    plus: workspace.seatPool?.purchased?.plus ?? 0,
    pro: workspace.seatPool?.purchased?.pro ?? 0,
  };
  const assigned = {
    plus:
      workspace.seatPool?.assigned?.plus ??
      normalizedAssignments.filter((assignment) => assignment.tier === 'plus')
        .length,
    pro:
      workspace.seatPool?.assigned?.pro ??
      normalizedAssignments.filter((assignment) => assignment.tier === 'pro')
        .length,
  };
  const seatPool = {
    companyId: workspace.companyId,
    purchased,
    assigned,
    available: {
      plus: Math.max(purchased.plus - assigned.plus, 0),
      pro: Math.max(purchased.pro - assigned.pro, 0),
    },
  };

  return {
    companyId: workspace.companyId,
    workspaceTier,
    memberCount: workspace.memberCount ?? 0,
    activeSeatCount: normalizedAssignments.length,
    seatPool,
    freeWorkspaceAiActionsPerDay:
      catalog.planSets.employer.workspaceFreePool?.aiActionsPerDay ?? 0,
  };
}

function resolveDefaultUsage(
  input: ResolveSubscriptionSnapshotInput,
  tier: PlanTier,
  seat: SeatAssignmentSummary | null,
  workspace: WorkspaceSubscriptionSummary | null,
  catalog: SubscriptionCatalog,
): UsageSnapshot {
  const dayKey = input.usage?.dayKey ?? new Date().toISOString().slice(0, 10);
  const cycleKey = input.usage?.credits?.cycleKey ?? dayKey.slice(0, 7);
  const resetAt = input.usage?.resetAt ?? `${dayKey}T23:59:59.999Z`;
  const used = input.usage?.used ?? 0;
  const reserved = input.usage?.reserved ?? 0;
  const committed = input.usage?.committed ?? used;
  const actionPackBalance = input.usage?.actionPackBalance ?? 0;

  if (input.planSet === 'individual') {
    const tierConfig = catalog.planSets.individual.tiers[tier];
    const limit =
      tierConfig.limits.aiCreditsPerDay ?? tierConfig.limits.aiActionsPerDay;
    const cycleLimit = tierConfig.limits.aiCreditsPerCycle ?? null;
    const dailyRemaining =
      limit === null
        ? null
        : Math.max(limit + actionPackBalance - used - reserved, 0);
    const cycleUsed = input.usage?.credits?.cycleUsed ?? used;
    const cycleReserved = input.usage?.credits?.cycleReserved ?? reserved;
    const cycleCommitted = input.usage?.credits?.cycleCommitted ?? committed;
    const cycleRemaining =
      cycleLimit === null
        ? null
        : Math.max(
            cycleLimit + actionPackBalance - cycleUsed - cycleReserved,
            0,
          );
    return {
      dayKey,
      meterSubject: 'individual_user',
      meterSubjectId: input.usage?.meterSubjectId ?? null,
      used,
      reserved,
      committed,
      remaining: minRemaining(dailyRemaining, cycleRemaining),
      limit,
      limitBehavior: tierConfig.limitBehavior,
      resetAt,
      actionPackBalance,
      credits: {
        unit: 'credits',
        cycleKey,
        cycleUsed,
        cycleReserved,
        cycleCommitted,
        cycleLimit,
        cycleRemaining,
        cycleResetAt:
          input.usage?.credits?.cycleResetAt ?? getCycleResetAt(cycleKey),
        dailyUsed: used,
        dailyReserved: reserved,
        dailyCommitted: committed,
        dailyLimit: limit,
        dailyRemaining,
        dailyResetAt: resetAt,
      },
    };
  }

  if (seat) {
    const tierConfig = catalog.planSets.employer.tiers[seat.tier];
    const limit = tierConfig.limits.aiActionsPerDay;
    return {
      dayKey,
      meterSubject: 'employer_seat',
      meterSubjectId:
        input.usage?.meterSubjectId ?? `${seat.companyId}:${seat.userId}`,
      used,
      reserved,
      committed,
      remaining:
        limit === null
          ? null
          : Math.max(limit + actionPackBalance - used - reserved, 0),
      limit,
      limitBehavior: tierConfig.limitBehavior,
      resetAt,
      actionPackBalance,
    };
  }

  if (workspace?.workspaceTier === 'free') {
    const limit =
      catalog.planSets.employer.workspaceFreePool?.aiActionsPerDay ?? 0;
    return {
      dayKey,
      meterSubject: 'employer_workspace',
      meterSubjectId: input.usage?.meterSubjectId ?? workspace.companyId,
      used,
      reserved,
      committed,
      remaining: Math.max(limit + actionPackBalance - used - reserved, 0),
      limit,
      limitBehavior: catalog.planSets.employer.tiers.free.limitBehavior,
      resetAt,
      actionPackBalance,
    };
  }

  return {
    dayKey,
    meterSubject: 'none',
    meterSubjectId: null,
    used,
    reserved,
    committed,
    remaining: 0,
    limit: 0,
    limitBehavior: 'hard_stop',
    resetAt,
    actionPackBalance,
    credits: null,
  };
}

export function resolveSubscriptionSnapshot(
  input: ResolveSubscriptionSnapshotInput,
  catalog: SubscriptionCatalog = subscriptionCatalog,
): SubscriptionSnapshot {
  const seat = buildSeatSummary(input.seat);
  const workspace = buildWorkspaceSummary(
    input.planSet,
    input.workspace,
    catalog,
  );
  const individualTier = normalizeLegacyPlanTier(
    input.tier ?? input.legacyPlan,
  );
  const tier =
    input.planSet === 'employer'
      ? (workspace?.workspaceTier ?? 'free')
      : individualTier;
  const usage = resolveDefaultUsage(input, tier, seat, workspace, catalog);
  const actionAllowances = (
    Object.keys(catalog.aiActions) as AIActionKey[]
  ).reduce<CustomerActionAllowanceMap>((result, actionKey) => {
    const baseline = buildCustomerAllowanceSnapshot({
      actionKey,
      tier,
      catalog,
    });
    if (!baseline) {
      return result;
    }

    const supplied = input.actionAllowances?.[actionKey];
    result[actionKey] =
      supplied?.windowKey === baseline.windowKey ? supplied : baseline;
    return result;
  }, {});

  return {
    planSet: input.planSet,
    tier,
    legacyPlan: input.legacyPlan ?? input.tier ?? tier,
    status: input.status ?? 'active',
    billingInterval: input.billingInterval ?? null,
    renewsAt: toIsoString(input.renewsAt),
    trialEndsAt: toIsoString(input.trialEndsAt),
    willRenew: input.willRenew ?? null,
    modelPolicy:
      input.planSet === 'employer'
        ? getModelPolicyForTier(seat?.tier ?? 'free')
        : getModelPolicyForTier(tier),
    source: input.source ?? 'local',
    managementUrl: input.managementUrl ?? null,
    store: input.store ?? null,
    lapsedFromTier: input.lapsedFromTier ?? null,
    seat,
    workspace,
    usage,
    actionAllowances,
  };
}

function isFeatureKey(
  catalog: SubscriptionCatalog,
  key: FeatureKey | AIActionKey | string,
): key is FeatureKey {
  return key in catalog.features;
}

function buildDecision(params: {
  snapshot: SubscriptionSnapshot;
  message: string;
  reasonCode: EntitlementReasonCode;
  featureKey?: FeatureKey;
  actionKey?: AIActionKey;
  promptKey?: EntitlementDecision['promptKey'];
  allowed: boolean;
  remainingActionsToday?: number | null;
  remainingCreditsToday?: number | null;
  remainingCreditsThisCycle?: number | null;
  estimatedCredits?: number | null;
  customerAllowance?: CustomerActionAllowanceSnapshot | null;
  limitBehavior?: EntitlementDecision['limitBehavior'];
  meterSubject?: EntitlementDecision['meterSubject'];
  modelPolicy?: EntitlementDecision['modelPolicy'];
  recommendedPlanSet?: EntitlementDecision['recommendedPlanSet'];
  recommendedTier?: EntitlementDecision['recommendedTier'];
}): EntitlementDecision {
  return {
    allowed: params.allowed,
    featureKey: params.featureKey,
    actionKey: params.actionKey,
    reasonCode: params.reasonCode,
    message: params.message,
    promptKey: params.promptKey ?? null,
    recommendedPlanSet: params.recommendedPlanSet ?? null,
    recommendedTier: params.recommendedTier ?? null,
    remainingActionsToday: params.remainingActionsToday ?? null,
    remainingCreditsToday: params.remainingCreditsToday ?? null,
    remainingCreditsThisCycle: params.remainingCreditsThisCycle ?? null,
    estimatedCredits: params.estimatedCredits ?? null,
    customerAllowance: params.customerAllowance ?? null,
    limitBehavior: params.limitBehavior ?? 'none',
    meterSubject: params.meterSubject ?? 'none',
    modelPolicy: params.modelPolicy ?? params.snapshot.modelPolicy,
  };
}

function mapFeatureDecisionToActionDecision(params: {
  snapshot: SubscriptionSnapshot;
  actionKey: AIActionKey;
  featureKey: FeatureKey;
  featureDecision: EntitlementDecision;
  modelPolicy: EntitlementDecision['modelPolicy'];
}): EntitlementDecision {
  return {
    allowed: false,
    actionKey: params.actionKey,
    featureKey: params.featureKey,
    reasonCode: params.featureDecision.reasonCode,
    message: params.featureDecision.message,
    promptKey: params.featureDecision.promptKey,
    recommendedPlanSet: params.featureDecision.recommendedPlanSet,
    recommendedTier: params.featureDecision.recommendedTier,
    remainingActionsToday: params.featureDecision.remainingActionsToday,
    remainingCreditsToday: params.featureDecision.remainingCreditsToday,
    remainingCreditsThisCycle: params.featureDecision.remainingCreditsThisCycle,
    estimatedCredits: params.featureDecision.estimatedCredits,
    customerAllowance: params.featureDecision.customerAllowance,
    limitBehavior: params.featureDecision.limitBehavior,
    meterSubject: params.featureDecision.meterSubject,
    modelPolicy: params.modelPolicy ?? params.snapshot.modelPolicy,
  };
}

export function evaluateEntitlement(
  snapshot: SubscriptionSnapshot,
  key: FeatureKey | AIActionKey,
  catalog: SubscriptionCatalog = subscriptionCatalog,
  options?: {
    complexity?: 'standard' | 'complex';
    subject?: 'feature' | 'ai_action';
  },
): EntitlementDecision {
  if (options?.subject !== 'ai_action' && isFeatureKey(catalog, key)) {
    const feature = catalog.features[key];
    if (snapshot.planSet !== feature.planSet) {
      const prompt = selectUpgradePrompt(snapshot, key, catalog, 'wrong_tier');
      return buildDecision({
        snapshot,
        featureKey: key,
        allowed: false,
        reasonCode: 'wrong_tier',
        message: `This feature is not available in the current ${snapshot.planSet} context.`,
        promptKey: prompt?.key ?? null,
        recommendedPlanSet: prompt?.recommendedPlanSet ?? null,
        recommendedTier: prompt?.recommendedTier ?? null,
      });
    }

    if (feature.planSet === 'employer' && !snapshot.workspace) {
      return buildDecision({
        snapshot,
        featureKey: key,
        allowed: false,
        reasonCode: 'workspace_required',
        message: 'Switch to a company workspace to use this feature.',
      });
    }

    const activeTier =
      feature.planSet === 'employer' && feature.usesWorkspaceTier
        ? (snapshot.workspace?.workspaceTier ?? 'free')
        : (snapshot.seat?.tier ?? snapshot.tier);
    const meetsTier = comparePlanTiers(activeTier, feature.minimumTier) >= 0;

    if (feature.requiresPaidSeat && !snapshot.seat) {
      const prompt = selectUpgradePrompt(
        snapshot,
        key,
        catalog,
        'seat_required',
      );
      return buildDecision({
        snapshot,
        featureKey: key,
        allowed: false,
        reasonCode: 'seat_required',
        message: 'This feature is available on assigned paid seats only.',
        promptKey: prompt?.key ?? null,
        recommendedPlanSet: prompt?.recommendedPlanSet ?? null,
        recommendedTier: prompt?.recommendedTier ?? null,
      });
    }

    if (!meetsTier) {
      const prompt = selectUpgradePrompt(snapshot, key, catalog, 'wrong_tier');
      return buildDecision({
        snapshot,
        featureKey: key,
        allowed: false,
        reasonCode: 'wrong_tier',
        message: `${feature.label} requires ${feature.minimumTier} or higher.`,
        promptKey: prompt?.key ?? null,
        recommendedPlanSet: prompt?.recommendedPlanSet ?? null,
        recommendedTier: prompt?.recommendedTier ?? null,
      });
    }

    const featureModelPolicy = catalog.aiActions[key as AIActionKey]
      ? selectModelPolicy(snapshot, key as AIActionKey, options, catalog)
      : snapshot.modelPolicy;

    return buildDecision({
      snapshot,
      featureKey: key,
      allowed: true,
      reasonCode: 'allowed',
      message: `${feature.label} is available.`,
      modelPolicy: featureModelPolicy,
    });
  }

  const actionKey = key as AIActionKey;
  const action = catalog.aiActions[actionKey];
  if (!action) {
    return buildDecision({
      snapshot,
      allowed: false,
      reasonCode: 'unknown',
      message: 'Unknown subscription entitlement.',
    });
  }

  const prompt = selectUpgradePrompt(snapshot, actionKey, catalog);
  const modelPolicy = selectModelPolicy(snapshot, actionKey, options, catalog);
  const customerAllowance = resolveCustomerActionAllowance(
    snapshot,
    actionKey,
    catalog,
  );

  if (action.featureKey) {
    const featureDecision = evaluateEntitlement(
      snapshot,
      action.featureKey,
      catalog,
      { ...options, subject: 'feature' },
    );

    // A declared customer allowance is an intentional sample of this action.
    // It bypasses only the action's feature tier; the mutation still goes
    // through allowance enforcement, internal metering, auth, and safety.
    if (!featureDecision.allowed && !customerAllowance) {
      return mapFeatureDecisionToActionDecision({
        snapshot,
        actionKey,
        featureKey: action.featureKey,
        featureDecision,
        modelPolicy,
      });
    }
  }

  if (action.planSet === 'onboarding') {
    return buildDecision({
      snapshot,
      actionKey,
      allowed: true,
      reasonCode: 'allowed',
      message: `${action.label} is available during onboarding.`,
      meterSubject: 'none',
      modelPolicy,
    });
  }

  if (snapshot.planSet !== action.planSet) {
    return buildDecision({
      snapshot,
      actionKey,
      allowed: false,
      reasonCode: 'wrong_tier',
      message: 'This AI action is not available in the current context.',
      promptKey: prompt?.key ?? null,
      recommendedPlanSet: prompt?.recommendedPlanSet ?? null,
      recommendedTier: prompt?.recommendedTier ?? null,
      meterSubject: 'none',
      modelPolicy,
      customerAllowance,
    });
  }

  if (
    action.requiresPaidSeat &&
    snapshot.planSet === 'employer' &&
    !snapshot.seat
  ) {
    const seatPrompt = selectUpgradePrompt(
      snapshot,
      actionKey,
      catalog,
      'seat_required',
    );
    return buildDecision({
      snapshot,
      actionKey,
      allowed: false,
      reasonCode: 'seat_required',
      message: 'AI is available on assigned employer seats only.',
      promptKey: seatPrompt?.key ?? null,
      recommendedPlanSet: seatPrompt?.recommendedPlanSet ?? null,
      recommendedTier: seatPrompt?.recommendedTier ?? null,
      meterSubject: 'none',
      modelPolicy,
    });
  }

  const metering = resolveMetering(snapshot, actionKey, catalog);
  const remaining = metering.remaining;
  const estimatedCredits = metering.estimatedUnits;
  // Token-metered actions (allowPartialCredits) may start on any positive
  // balance because their reservation and final charge cap at the remaining
  // credits; fixed-price actions must afford the full estimate up front.
  const requiredCredits = action.allowPartialCredits
    ? Math.min(estimatedCredits, 1)
    : estimatedCredits;

  if (customerAllowance && customerAllowance.remaining < 1) {
    const allowancePrompt = selectUpgradePrompt(
      snapshot,
      actionKey,
      catalog,
      'action_allowance_reached',
    );
    return buildDecision({
      snapshot,
      actionKey,
      featureKey: action.featureKey,
      allowed: false,
      reasonCode: 'action_allowance_reached',
      message: `You’ve used your included ${customerAllowance.label} for this ${customerAllowance.cadence}.`,
      promptKey: allowancePrompt?.key ?? prompt?.key ?? null,
      recommendedPlanSet:
        allowancePrompt?.recommendedPlanSet ??
        prompt?.recommendedPlanSet ??
        null,
      recommendedTier:
        allowancePrompt?.recommendedTier ?? prompt?.recommendedTier ?? null,
      remainingActionsToday: 0,
      remainingCreditsToday: metering.dailyRemaining,
      remainingCreditsThisCycle: metering.cycleRemaining,
      estimatedCredits,
      customerAllowance,
      limitBehavior: metering.limitBehavior,
      meterSubject: metering.meterSubject,
      modelPolicy,
    });
  }

  if (metering.meterSubject === 'none') {
    const reasonCode: EntitlementReasonCode =
      snapshot.planSet === 'employer' ? 'seat_required' : 'wrong_tier';
    const blockedPrompt = selectUpgradePrompt(
      snapshot,
      actionKey,
      catalog,
      reasonCode,
    );
    return buildDecision({
      snapshot,
      actionKey,
      allowed: false,
      reasonCode,
      message:
        reasonCode === 'seat_required'
          ? 'This workspace action needs an assigned AI seat.'
          : 'This action is not available on the current plan.',
      promptKey: blockedPrompt?.key ?? null,
      recommendedPlanSet: blockedPrompt?.recommendedPlanSet ?? null,
      recommendedTier: blockedPrompt?.recommendedTier ?? null,
      remainingActionsToday: remaining,
      remainingCreditsToday: metering.dailyRemaining,
      remainingCreditsThisCycle: metering.cycleRemaining,
      estimatedCredits,
      customerAllowance,
      limitBehavior: metering.limitBehavior,
      meterSubject: metering.meterSubject,
      modelPolicy,
    });
  }

  if (remaining !== null && remaining < requiredCredits) {
    if (
      snapshot.planSet === 'employer' &&
      metering.meterSubject === 'employer_workspace'
    ) {
      const limitPrompt = selectUpgradePrompt(
        snapshot,
        actionKey,
        catalog,
        'workspace_ai_limit_reached',
      );
      return buildDecision({
        snapshot,
        actionKey,
        allowed: false,
        reasonCode: 'workspace_ai_limit_reached',
        message: 'Your workspace has used today’s shared AI actions.',
        promptKey: limitPrompt?.key ?? null,
        recommendedPlanSet: limitPrompt?.recommendedPlanSet ?? null,
        recommendedTier: limitPrompt?.recommendedTier ?? null,
        remainingActionsToday: 0,
        remainingCreditsToday: metering.dailyRemaining,
        remainingCreditsThisCycle: metering.cycleRemaining,
        estimatedCredits,
        customerAllowance,
        limitBehavior: metering.limitBehavior,
        meterSubject: metering.meterSubject,
        modelPolicy,
      });
    }

    if (metering.limitBehavior === 'hard_stop') {
      const limitPrompt = selectUpgradePrompt(
        snapshot,
        actionKey,
        catalog,
        'ai_daily_limit_reached',
      );
      return buildDecision({
        snapshot,
        actionKey,
        allowed: false,
        reasonCode: 'ai_daily_limit_reached',
        message:
          snapshot.planSet === 'individual'
            ? 'This request exceeds the AI use currently available on your plan.'
            : 'You have used today’s AI actions.',
        promptKey: limitPrompt?.key ?? null,
        recommendedPlanSet: limitPrompt?.recommendedPlanSet ?? null,
        recommendedTier: limitPrompt?.recommendedTier ?? null,
        remainingActionsToday: Math.max(remaining, 0),
        remainingCreditsToday: metering.dailyRemaining,
        remainingCreditsThisCycle: metering.cycleRemaining,
        estimatedCredits,
        customerAllowance,
        limitBehavior: metering.limitBehavior,
        meterSubject: metering.meterSubject,
        modelPolicy,
      });
    }

    if (metering.limitBehavior === 'soft_throttle') {
      const limitPrompt = selectUpgradePrompt(
        snapshot,
        actionKey,
        catalog,
        'ai_daily_limit_reached',
      );
      return buildDecision({
        snapshot,
        actionKey,
        allowed: true,
        reasonCode: 'ai_daily_limit_reached',
        message:
          'You are over today’s included AI actions. Requests may be throttled.',
        promptKey: limitPrompt?.key ?? null,
        recommendedPlanSet: limitPrompt?.recommendedPlanSet ?? null,
        recommendedTier: limitPrompt?.recommendedTier ?? null,
        remainingActionsToday: Math.max(remaining, 0),
        remainingCreditsToday: metering.dailyRemaining,
        remainingCreditsThisCycle: metering.cycleRemaining,
        estimatedCredits,
        customerAllowance,
        limitBehavior: metering.limitBehavior,
        meterSubject: metering.meterSubject,
        modelPolicy,
      });
    }

    return buildDecision({
      snapshot,
      actionKey,
      allowed: true,
      reasonCode: 'fair_use_slowdown',
      message: 'Heavy usage detected. Responses may be slower until tomorrow.',
      promptKey:
        selectUpgradePrompt(snapshot, actionKey, catalog, 'fair_use_slowdown')
          ?.key ?? null,
      remainingActionsToday: Math.max(remaining, 0),
      remainingCreditsToday: metering.dailyRemaining,
      remainingCreditsThisCycle: metering.cycleRemaining,
      estimatedCredits,
      customerAllowance,
      limitBehavior: metering.limitBehavior,
      meterSubject: metering.meterSubject,
      modelPolicy,
    });
  }

  return buildDecision({
    snapshot,
    actionKey,
    allowed: true,
    reasonCode: 'allowed',
    message: `${action.label} is available.`,
    remainingActionsToday: remaining,
    remainingCreditsToday: metering.dailyRemaining,
    remainingCreditsThisCycle: metering.cycleRemaining,
    estimatedCredits,
    customerAllowance,
    limitBehavior: metering.limitBehavior,
    meterSubject: metering.meterSubject,
    modelPolicy,
  });
}
